/**
 * The reports: short or long, for one facility or all of them, one trade or
 * every trade. What is on screen is what prints — the browser's print dialog
 * makes the file, as on the work-orders report.
 *
 * The short report is a line per facility and trade: condition, when and by
 * whom it was last inspected, what is open and what next year is forecast to
 * cost. The long report adds, for each, the open findings, every inspection
 * with what it found, the repairs, and the forecast's working.
 */
import { useState } from "react";
import { Button, Field, Select } from "../../design/components";
import {
  BAND_LABEL,
  FORM_LABEL,
  RESULT_LABEL,
  TRADES,
  defectsOf,
  reportLines,
  scoreOf,
  type InspectionStore,
  type ReportLength,
  type TradeId,
} from "../../domain/assetInspections";
import { FACILITY_LIST } from "../../domain/facilityRegister";
import { STATUS_LABEL, type WorkOrder } from "../../domain/workOrders";
import { formatDate, formatDateTime } from "../../lib/format";
import { t, tn } from "../../i18n";
import { itemText, sar } from "./labels";
import s from "./FacilityInspections.module.css";

export function InspectionReports({
  store,
  orders,
  site,
  who,
  initialTrade,
}: {
  store: InspectionStore;
  orders: readonly WorkOrder[];
  site: string;
  who: string;
  initialTrade: TradeId | "all";
}) {
  const [facilityId, setFacilityId] = useState<string>("all");
  const [trade, setTrade] = useState<TradeId | "all">(initialTrade);
  const [length, setLength] = useState<ReportLength>("short");
  const now = new Date();
  const lines = reportLines(store, orders, { facilityId, trade, length }, now);
  const facilityName = facilityId === "all" ? t("All facilities") : `${facilityId} · ${FACILITY_LIST.find((f) => f.id === facilityId)?.name ?? ""}`;
  const tradeName = trade === "all" ? t("All trades") : t(TRADES.find((x) => x.id === trade)!.label);
  const openFindings = lines.reduce((n, l) => n + l.standing.open.length, 0);
  const openOrders = lines.reduce((n, l) => n + l.openOrders.length, 0);
  const budget = lines.reduce((n, l) => n + (l.forecast?.budget ?? 0), 0);
  const sample = lines.some((l) => l.standing.basis?.sample);

  return (
    <div className={s.page}>
      <div className={`${s.bar} ${s.noPrint}`}>
        <Field label="Facility">
          <Select value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
            <option value="all">{t("All facilities")}</option>
            {FACILITY_LIST.map((f) => (
              <option key={f.id} value={f.id}>
                {f.id} · {f.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Trade">
          <Select value={trade} onChange={(e) => setTrade(e.target.value as TradeId | "all")}>
            <option value="all">{t("All trades")}</option>
            {TRADES.map((x) => (
              <option key={x.id} value={x.id}>
                {t(x.label)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Report">
          <Select value={length} onChange={(e) => setLength(e.target.value as ReportLength)}>
            <option value="short">{t("Short report — condition summary")}</option>
            <option value="long">{t("Long report — findings, inspections and repairs")}</option>
          </Select>
        </Field>
        <span className={s.spacer} />
        <Button variant="primary" onClick={() => window.print()} disabled={lines.length === 0}>
          {t("Print or save as PDF")}
        </Button>
      </div>

      <article className={s.report} data-testid="inspection-report">
        <header className={s.reportHead}>
          <div className={s.reportTitle}>
            {length === "short" ? t("Facility condition report") : t("Facility inspection report — detailed")}
          </div>
          <div className={s.small}>
            {site} · {facilityName} · {tradeName}
          </div>
          <div className={`${s.small} ${s.muted}`}>
            {t("Prepared {when} by {who}", { when: formatDateTime(now.toISOString()), who })}
            {sample && (
              <>
                {" · "}
                <span className={s.sampleTag}>{t("SAMPLE")}</span> {t("includes sample records")}
              </>
            )}
          </div>
        </header>

        {lines.length === 0 ? (
          <p className={s.muted}>{t("Nothing has been inspected or repaired in this scope yet.")}</p>
        ) : (
          <>
            <div className={s.tiles}>
              <div className={s.tile}>
                <span className={s.tileValue}>{lines.length}</span>
                <span className={s.tileLabel}>{t("facility and trade lines")}</span>
              </div>
              <div className={s.tile}>
                <span className={s.tileValue}>{openFindings}</span>
                <span className={s.tileLabel}>{t("open findings")}</span>
              </div>
              <div className={s.tile}>
                <span className={s.tileValue}>{openOrders}</span>
                <span className={s.tileLabel}>{t("open work orders")}</span>
              </div>
              <div className={s.tile}>
                <span className={s.tileValue}>{sar(budget)}</span>
                <span className={s.tileLabel}>{t("forecast, next 12 months")}</span>
              </div>
            </div>

            <div className={s.scroll}>
              <table className={s.grid}>
                <thead>
                  <tr>
                    <th>{t("Facility")}</th>
                    <th>{t("Trade")}</th>
                    <th>{t("Condition")}</th>
                    <th>{t("Last inspected")}</th>
                    <th>{t("By")}</th>
                    <th className={s.num}>{t("Open findings")}</th>
                    <th className={s.num}>{t("Open work orders")}</th>
                    <th className={s.num}>{t("Forecast")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={`${l.facility.id}-${l.trade.id}`}>
                      <td>
                        <span className={s.mono}>{l.facility.id}</span> {l.facility.name}
                      </td>
                      <td>{t(l.trade.label)}</td>
                      <td>
                        {l.standing.band ? (
                          <span className={`${s.band} ${s[l.standing.band]}`}>
                            {l.standing.score} · {t(BAND_LABEL[l.standing.band])}
                          </span>
                        ) : (
                          <span className={`${s.band} ${s.none}`}>{t("not inspected")}</span>
                        )}
                      </td>
                      <td className={s.small}>
                        {l.standing.latest ? `${formatDate(l.standing.latest.at)} · ${t(FORM_LABEL[l.standing.latest.form])}` : "—"}
                      </td>
                      <td className={s.small}>{l.standing.latest?.company ?? "—"}</td>
                      <td className={s.num}>{l.standing.open.length}</td>
                      <td className={s.num}>{l.openOrders.length}</td>
                      <td className={s.num}>{l.forecast ? sar(l.forecast.budget) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {length === "long" &&
              lines.map((l) => (
                <section key={`d-${l.facility.id}-${l.trade.id}`} className={s.reportBlock}>
                  <h3>
                    {l.facility.id} · {l.facility.name} — {t(l.trade.label)}
                  </h3>
                  {l.standing.open.length > 0 && (
                    <>
                      <div className={s.sectionTitle}>{t("Open findings")}</div>
                      <ul className={s.list}>
                        {l.standing.open.map((o) => (
                          <li key={o.fault.itemId} className={s.finding}>
                            <span className={`${s.sev} ${s[`sev_${o.fault.result}`]}`}>{t(RESULT_LABEL[o.fault.result])}</span>
                            <span>{itemText(l.trade, o.fault.itemId)}</span>
                            {o.fault.note && <span className={s.muted}>— {o.fault.note}</span>}
                            {o.fault.repairType && <span className={s.muted}>· {t(o.fault.repairType)}</span>}
                            <span className={s.mono}>
                              {o.fault.workOrderNumber ? o.fault.workOrderNumber : t("no work order yet")}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {l.inspections.length > 0 && (
                    <>
                      <div className={s.sectionTitle}>{t("Inspections")}</div>
                      <table className={s.grid}>
                        <tbody>
                          {l.inspections
                            .slice()
                            .reverse()
                            .map((r) => (
                              <tr key={r.id}>
                                <td className={s.small}>{formatDate(r.at)}</td>
                                <td className={s.small}>{t(FORM_LABEL[r.form])}</td>
                                <td className={s.small}>
                                  {r.company} · {r.inspector}
                                </td>
                                <td className={s.num}>{scoreOf(r)}</td>
                                <td className={s.small}>
                                  {defectsOf(r).length === 0
                                    ? t("nothing found")
                                    : defectsOf(r)
                                        .map((f) => `${t(RESULT_LABEL[f.result])}: ${itemText(l.trade, f.itemId)}`)
                                        .join("; ")}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  {l.openOrders.length > 0 && (
                    <>
                      <div className={s.sectionTitle}>{t("Open work orders")}</div>
                      <ul className={s.list}>
                        {l.openOrders.map((o) => (
                          <li key={o.id} className={s.small}>
                            <span className={s.mono}>{o.number}</span> · {t(STATUS_LABEL[o.status])} · {o.title}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {l.repairs.length > 0 && (
                    <>
                      <div className={s.sectionTitle}>{tn(l.repairs.length, "{n} repair on record", "{n} repairs on record")}</div>
                      <table className={s.grid}>
                        <tbody>
                          {l.repairs
                            .slice()
                            .reverse()
                            .slice(0, 12)
                            .map((r) => (
                              <tr key={r.id}>
                                <td className={s.small}>{formatDate(r.openedAt)}</td>
                                <td className={s.small}>{t(r.repairType)}</td>
                                <td className={s.num}>{r.cost !== undefined ? sar(r.cost) : "—"}</td>
                                <td className={s.small}>{r.sample ? t("sample") : r.source}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  {l.forecast && (
                    <p className={s.small}>
                      {t(
                        "Forecast: {recent} repairs in the last two years, {rate} a year, × {factor} for the condition, + {backlog} for open findings, at {avg} each = {budget}.",
                        {
                          recent: l.forecast.recent,
                          rate: l.forecast.ratePerYear,
                          factor: l.forecast.factor,
                          backlog: l.forecast.backlog,
                          avg: sar(l.forecast.averageCost),
                          budget: sar(l.forecast.budget),
                        },
                      )}
                    </p>
                  )}
                </section>
              ))}
          </>
        )}
      </article>
    </div>
  );
}
